import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { navigate } from '../router';
import { navigateAfterLogin, stytch } from '../auth/session.ts';
import { LitPage } from '../components/lit-page.ts';

import '../components/luna-auth-card/luna-auth-card';
import '../components/luna-button/luna-button';

const SESSION_DURATION_MINUTES = 60 * 24 * 90; // 90 days

@customElement('app-reset-password')
export class AppResetPassword extends LitPage {
  @state() private password = '';
  @state() private confirmPassword = '';
  @state() private busy = false;
  @state() private errorMessage = '';

  private token: string | null = null;

  firstUpdated() {
    this.token = new URLSearchParams(window.location.search).get('token');
    if (!this.token) {
      navigate('/login');
    }
  }

  private async onSubmit(e: Event) {
    e.preventDefault();
    if (this.busy || !this.token) return;

    if (this.password !== this.confirmPassword) {
      this.errorMessage = 'Those passwords don\'t match. Please try again.';
      return;
    }

    this.busy = true;
    this.errorMessage = '';
    try {
      await stytch.passwords.resetByEmail({
        token: this.token,
        password: this.password,
        session_duration_minutes: SESSION_DURATION_MINUTES,
      });
      navigateAfterLogin();
    } catch (err) {
      const errorType = (err as { error_type?: string })?.error_type;
      if (errorType === 'weak_password' || errorType === 'breached_password') {
        this.errorMessage = 'Please choose a stronger password and try again.';
      } else if (errorType === 'reset_password' || errorType === 'unable_to_auth_reset_password_token') {
        this.errorMessage = 'This reset link has expired. Please request a new one.';
      } else {
        this.errorMessage = 'We couldn\'t reset your password. Please try again.';
      }
    } finally {
      this.busy = false;
    }
  }

  content() {
    return html`
      <luna-auth-card>
        <h1 class="mbw-type-title-section">Choose a new password</h1>

        <div class="auth-error" role="alert" ?hidden=${!this.errorMessage}>
          ${this.errorMessage}
        </div>

        <form @submit=${this.onSubmit}>
          <div>
            <label for="reset-password">New password</label>
            <input
              id="reset-password"
              type="password"
              autocomplete="new-password"
              required
              .value=${this.password}
              @input=${(e: Event) => { this.password = (e.target as HTMLInputElement).value; }}
            />
          </div>

          <div>
            <label for="reset-confirm">Confirm new password</label>
            <input
              id="reset-confirm"
              type="password"
              autocomplete="new-password"
              required
              .value=${this.confirmPassword}
              @input=${(e: Event) => { this.confirmPassword = (e.target as HTMLInputElement).value; }}
            />
          </div>

          <button type="submit" class="auth-submit-ghost" tabindex="-1" aria-hidden="true">
            Save new password
          </button>
          <luna-button full ?disabled=${this.busy} @click=${this.onSubmit}>
            <span>${this.busy ? 'Saving…' : 'Save new password'}</span>
          </luna-button>
        </form>

        <div class="auth-links">
          <button class="auth-link" type="button" @click=${() => navigate('/login')}>
            Back to sign in
          </button>
        </div>
      </luna-auth-card>
    `;
  }
}
